import { api } from "~/utils/api"
import { formatForecast, formatNumberWithSuffix } from "~/utils/formatting"

import { Card, CardContent, CardHeader, CardTitle } from "../reusable/Card"
import { Tooltip } from "../reusable/Tooltip"
import { ArrowDown, ArrowUp } from "@phosphor-icons/react"

export type ComparisonCardRow = {
  title: string
  subtitle?: string
  badge?: string
  value?: number
  currency?: boolean
  inPercentage?: boolean
  comparison?: number
  cell?: React.ReactNode
  comparisonTooltip?: string
}

interface ComparisonCardProps {
  title?: string
  rows: ComparisonCardRow[]
  horizontal?: boolean
  className?: string
}

function comparisonInPercentage(value: number, comparison: number): number {
  return ((value - comparison) / comparison) * 100
}

const ComparisonCard: React.FC<ComparisonCardProps> = ({
  title,
  rows,
  horizontal = false,
  className,
}) => {
  const { data: company } = api.company.getCompany.useQuery()

  const calculateValue = (row: ComparisonCardRow, value?: number) => {
    if (row.inPercentage) {
      return value !== undefined ? value.toFixed(1) + "%" : "-"
    }

    if (row.currency) {
      return value !== undefined
        ? formatNumberWithSuffix(value, 1) + " " + (company?.currency ?? "DKK")
        : "-"
    }

    return value !== undefined ? formatForecast(value) : "-"
  }

  return (
    <Card className={`h-fit ${className}`}>
      {title && (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="flex align-middle h-full">
        <div
          className={`${horizontal ? `grid grid-cols-${rows.length} items-center gap-6 w-full` : "flex flex-col w-full divide-y divide-dashed divide-border-base"}`}
        >
          {rows.map((row, index) => (
            <div key={index} className={`${!horizontal ? "py-3" : ""} first:pt-0 last:pb-0`}>
              <div className="flex flex-row gap-1 items-center">
                <div className="text-md text-fg-subtle">{row.title}</div>
                {row.badge && (
                  <div className="bg-subtle rounded-md border border-base px-1 text-fg-subtle text-sm font-medium">
                    {row.badge}
                  </div>
                )}
              </div>
              {row.subtitle && <div className="text-xs text-fg-subtle">{row.subtitle}</div>}
              <div className="flex flex-row gap-1 items-end">
                <div className="text-fg-base old-heading-xl">
                  {row.cell || calculateValue(row, row.value)}
                </div>
                <div className="flex flex-row gap-0.5 mb-1">
                  {row.value !== undefined &&
                    row.comparison !== undefined &&
                    (comparisonInPercentage(row.value, row.comparison) >= 0 ? (
                      <ArrowUp className="mb-1 inline-block h-4 w-4 align-middle text-green-400" />
                    ) : (
                      <ArrowDown className="mb-1 inline-block h-4 w-4 align-middle text-red-500" />
                    ))}
                  {row.comparison !== undefined && (
                    <Tooltip
                      disableHoverableContent
                      title={
                        row.comparisonTooltip
                          ? `${row.comparisonTooltip} ${calculateValue(row, row.comparison)}`
                          : `Compared to the previous period. Last period it was ${calculateValue(row, row.comparison)}.`
                      }
                    >
                      <div className="text-md font-medium">
                        {row.value && row.comparison
                          ? comparisonInPercentage(row.value, row.comparison).toFixed(1) + "%"
                          : "- %"}
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

export default ComparisonCard
